/**
 * A repeating timer that keeps its cadence while the tab is HIDDEN.
 *
 * `window.setInterval` is the wrong clock for a presence heartbeat: once a
 * page has been hidden for five minutes, Chrome's intensive wake-up
 * throttling aligns chained main-thread timers to ONE wake per minute
 * (Safari throttles hidden tabs comparably), while peers evict anyone silent
 * for `OFFLINE_AFTER_MS` = 15 s. A presenter who switched to another tab for
 * a while therefore flapped offline on every peer — avatar gone, presenter
 * flag cleared and re-elected around them — and came back a minute later.
 * Unity never sees this: its heartbeat is a coroutine in a desktop process.
 *
 * Dedicated-worker timers are not subject to that throttling, and a `message`
 * from the worker runs on the main thread without the timer-chain penalty,
 * so the worker is the clock and the main thread does the work. The worker
 * is an inline module (a `blob:` URL), so no build or public asset is needed;
 * where workers or blob URLs are unavailable or a CSP refuses the blob, the
 * `setInterval` fallback keeps the old behaviour.
 */

const K_WORKER_SOURCE = [
  "let timer = null;",
  "onmessage = (event) => {",
  "  if (timer !== null) { clearInterval(timer); timer = null; }",
  "  const ms = Number(event.data);",
  "  if (ms > 0) timer = setInterval(() => postMessage(0), ms);",
  "};",
].join("\n");

export function startBackgroundInterval(callback: () => void, intervalMs: number): () => void {
  const worker = tryCreateWorker();
  if (worker) {
    const { instance, url } = worker;
    instance.onmessage = () => callback();
    instance.postMessage(intervalMs);
    return () => {
      instance.onmessage = null;
      instance.postMessage(0);
      instance.terminate();
      URL.revokeObjectURL(url);
    };
  }
  const id = window.setInterval(callback, intervalMs);
  return () => window.clearInterval(id);
}

function tryCreateWorker(): { instance: Worker; url: string } | null {
  if (typeof Worker !== "function" || typeof Blob !== "function" || typeof URL === "undefined") {
    return null;
  }
  let url: string | null = null;
  try {
    url = URL.createObjectURL(new Blob([K_WORKER_SOURCE], { type: "text/javascript" }));
    return { instance: new Worker(url), url };
  } catch (err: unknown) {
    // A CSP without `worker-src blob:` throws here synchronously.
    if (url) URL.revokeObjectURL(url);
    console.warn("[backgroundInterval] worker unavailable, falling back to setInterval", err);
    return null;
  }
}
